import "../../CommonImports";
import "../../Core/core.css";
import * as React from "react";
import { createMergedRef } from '../../Util';
// We need to monitor fine grained changes, especially when the list
// has horizontal scroll. You dont get 100% visible ever.
const defaultThreshold = [];
for (let index = 0; index <= 100; index++) {
    defaultThreshold.push(index / 100);
}
class IntersectionContextImpl {
    constructor() {
        this.callbacks = [];
        this.pending = [];
        this.rootMargin = 0;
        this.root = document.body;
        this.onIntersect = (entries) => {
            if (this.observer) {
                for (const callback of this.callbacks) {
                    callback(entries, this.observer);
                }
            }
        };
    }
    connect(root, rootMargin = 0, threshold = defaultThreshold) {
        this.observer = new IntersectionObserver(this.onIntersect, { root, rootMargin: rootMargin + "px", threshold });
        this.rootMargin = rootMargin;
        this.root = root;
        for (const element of this.pending) {
            this.observer.observe(element);
        }
    }
    disconnect() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
    observe(element) {
        if (this.observer) {
            this.observer.observe(element);
        }
        else {
            this.pending.push(element);
        }
    }
    register(callback) {
        this.callbacks.push(callback);
    }
    unobserve(element) {
        const elementIndex = this.pending.indexOf(element);
        if (elementIndex >= 0) {
            this.pending.splice(elementIndex, 1);
        }
        if (this.observer) {
            this.observer.unobserve(element);
        }
    }
    unregister(callback) {
        const callbackIndex = this.callbacks.indexOf(callback);
        if (callbackIndex >= 0) {
            this.callbacks.splice(callbackIndex, 1);
        }
    }
}
export const IntersectionContext = React.createContext(new IntersectionContextImpl());
/**
 * The Intersection is used to observe the changes of visibility in the children
 * of the rootElement. It also will notify the caller when the rootElement is
 * scrolled. It will pass an empty array of entries in the scorlling case.
 */
export class Intersection extends React.Component {
    constructor() {
        super(...arguments);
        this.mergedRef = createMergedRef();
        this.rootElement = React.createRef();
        this.state = new IntersectionContextImpl();
        this.onScroll = (event) => {
            this.state.onIntersect([]);
        };
    }
    // Render the provider around a SINGLE child. This is the element that is scrollable.
    render() {
        const child = React.Children.only(this.props.children);
        let onScroll;
        if (child.props.onScroll) {
            onScroll = (event) => {
                if (child.props.onScroll) {
                    child.props.onScroll(event);
                }
                this.onScroll(event);
            };
        }
        else {
            onScroll = this.onScroll;
        }
        return (React.createElement(IntersectionContext.Provider, { value: this.state }, React.cloneElement(child, Object.assign(Object.assign({}, child.props), { ref: this.mergedRef(this.rootElement, child.ref), onScroll }), child.props.children)));
    }
    componentDidMount() {
        const { observationElement, rootElement } = this.props;
        let connectElement = null;
        if (rootElement) {
            if (typeof rootElement === "string") {
                connectElement = document.querySelector(rootElement);
            }
            else if (typeof rootElement === "function") {
                connectElement = rootElement();
            }
            else {
                connectElement = rootElement;
            }
            if (connectElement) {
                connectElement.addEventListener("scroll", this.onScroll);
                this.externalElement = connectElement;
            }
        }
        else if (this.rootElement) {
            connectElement = this.rootElement.current;
        }
        if (connectElement) {
            this.state.connect(connectElement, this.props.rootMargin, this.props.threshold);
            // Allow the creator of the intersection to observe intersection events.
            if (this.props.onIntersect) {
                this.state.register(this.props.onIntersect);
            }
            if (observationElement) {
                let observeElement;
                if (typeof observationElement === "string") {
                    observeElement = document.querySelector(observationElement);
                }
                else if (typeof observationElement === "function") {
                    observeElement = observationElement();
                }
                else {
                    observeElement = observationElement;
                }
                if (observeElement) {
                    this.state.observe(observeElement);
                }
            }
        }
    }
    componentWillUnmount() {
        if (this.externalElement) {
            this.externalElement.removeEventListener("scroll", this.onScroll);
        }
        this.state.disconnect();
    }
}
